import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Skeleton,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { StoreSettings } from '../../types';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { invalidateStoreSettings } from '../../hooks/useStoreSettings';
import { formatCurrency } from '../../utils/pricing';

const AdminSettingsPage: React.FC = () => {
  const [store, setStore] = useState<StoreSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.SITE_SETTINGS}/store`);
      setStore(response.data.store as StoreSettings);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to load store settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!store) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await apiClient.put(`${API_ENDPOINTS.SITE_SETTINGS}/store`, { store });
      setStore(response.data.store as StoreSettings);
      invalidateStoreSettings();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to save store settings'));
    } finally {
      setSaving(false);
    }
  };

  const setNumber = (field: keyof StoreSettings) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setStore((prev) => (prev ? { ...prev, [field]: Number(e.target.value) || 0 } : prev));

  if (loading || !store) {
    return (
      <Box>
        <Skeleton width={220} height={40} />
        <Skeleton variant="rounded" height={260} sx={{ mt: 2 }} />
      </Box>
    );
  }

  const shippingSummary =
    store.shippingCharge <= 0
      ? 'Delivery is free on every order.'
      : store.freeShippingThreshold > 0
        ? `Customers pay ${formatCurrency(store.shippingCharge)} delivery, free above ${formatCurrency(store.freeShippingThreshold)}.`
        : `Customers pay ${formatCurrency(store.shippingCharge)} delivery on every order.`;

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Store settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Delivery charges, order limits and payment availability
          </Typography>
        </Box>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved — new orders use these rules immediately.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Delivery
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {shippingSummary}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Delivery charge (₹)"
            type="number"
            value={store.shippingCharge}
            onChange={setNumber('shippingCharge')}
            helperText="Set 0 for always-free delivery"
          />
          <TextField
            label="Free delivery above (₹)"
            type="number"
            value={store.freeShippingThreshold}
            onChange={setNumber('freeShippingThreshold')}
            helperText="Based on cart value before any coupon"
            disabled={store.shippingCharge <= 0}
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Orders & payment
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <TextField
            label="Minimum order value (₹)"
            type="number"
            value={store.minOrderValue}
            onChange={setNumber('minOrderValue')}
            helperText="0 = no minimum"
          />
          <TextField
            label="Low stock alert at (units)"
            type="number"
            value={store.lowStockThreshold}
            onChange={setNumber('lowStockThreshold')}
            helperText="Used by the dashboard low-stock panel"
          />
          <TextField
            label="Online payment discount (%)"
            type="number"
            value={store.onlinePaymentDiscountPercent}
            onChange={setNumber('onlinePaymentDiscountPercent')}
            helperText="Percent off for paying online (0 = off). COD orders get no discount."
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <FormControlLabel
          control={
            <Switch
              checked={store.codEnabled}
              onChange={(e) => setStore({ ...store, codEnabled: e.target.checked })}
            />
          }
          label="Offer Cash on Delivery"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Turning this off hides COD at checkout and rejects new COD orders. Make sure online
          payment is live before disabling it.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Review reward
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {store.reviewRewardPercent > 0
            ? `When you approve a review, the customer is emailed a one-time ${store.reviewRewardPercent}% off coupon${store.reviewRewardMaxDiscount > 0 ? ` (capped at ${formatCurrency(store.reviewRewardMaxDiscount)})` : ''}, valid ${store.reviewRewardValidityDays} days.`
            : 'Off — approving a review sends no coupon. Set a percent to start rewarding reviewers.'}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Discount (%)"
            type="number"
            value={store.reviewRewardPercent}
            onChange={setNumber('reviewRewardPercent')}
            helperText="0 = no reward. Sent once per review, on first approval."
          />
          <TextField
            label="Valid for (days)"
            type="number"
            value={store.reviewRewardValidityDays}
            onChange={setNumber('reviewRewardValidityDays')}
            disabled={store.reviewRewardPercent <= 0}
          />
          <TextField
            label="Max discount (₹)"
            type="number"
            value={store.reviewRewardMaxDiscount}
            onChange={setNumber('reviewRewardMaxDiscount')}
            helperText="0 = no cap"
            disabled={store.reviewRewardPercent <= 0}
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Welcome coupon
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {store.welcomeCouponPercent > 0
            ? `New customers get a unique one-time HELLO-XXXX code for ${store.welcomeCouponPercent}% off${store.welcomeCouponMaxDiscount > 0 ? ` (capped at ${formatCurrency(store.welcomeCouponMaxDiscount)})` : ''}, valid ${store.welcomeCouponValidityDays} days from signup.`
            : 'Off — new signups get no welcome coupon. Set a percent to start welcoming customers with a discount.'}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            label="Discount (%)"
            type="number"
            value={store.welcomeCouponPercent}
            onChange={setNumber('welcomeCouponPercent')}
            helperText="0 = no welcome coupon. Each signup gets its own trackable code."
          />
          <TextField
            label="Valid for (days)"
            type="number"
            value={store.welcomeCouponValidityDays}
            onChange={setNumber('welcomeCouponValidityDays')}
            disabled={store.welcomeCouponPercent <= 0}
          />
          <TextField
            label="Max discount (₹)"
            type="number"
            value={store.welcomeCouponMaxDiscount}
            onChange={setNumber('welcomeCouponMaxDiscount')}
            helperText="0 = no cap"
            disabled={store.welcomeCouponPercent <= 0}
          />
        </Box>
      </Paper>

      <Button variant="contained" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </Box>
  );
};

export default AdminSettingsPage;
